/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop -- Inline style values are computed dynamically per-edge based on connection geometry; will migrate to css prop when refactoring layout. */
/* eslint-disable @atlaskit/design-system/ensure-design-token-usage -- Hard-coded colours for the dark graph edge theme; will migrate to design tokens in a follow-up. */
/**
 * FloatingEdge - Straight grey edge with dynamic connection points and an
 * optional relationship-verb label.
 *
 * Verb-label visibility:
 *   The label is hidden by default. It is revealed only when the user hovers
 *   a **non-central** node (a one-hop neighbour of the active/centre node)
 *   and this edge is the connection between that neighbour and the
 *   active/centre node. Hovering the centre/active node itself does not
 *   reveal labels — that would surface every relationship at once and
 *   feels overwhelming.
 *
 *   The label fades in/out smoothly (it is always rendered with opacity
 *   driven by the visibility predicate) so it never "pops" on or off.
 *
 * Visual styling:
 *   The label pill mirrors the colour palette of the node hover chip in
 *   `StackedObjectNode.tsx` (dark surface, subtle border, low-emphasis
 *   text) — only the colours are mirrored, not the sizing.
 *
 * @jsxRuntime classic
 * @jsx jsx
 */

import React, { memo } from 'react';

import {
	EdgeLabelRenderer,
	useInternalNode,
	type EdgeProps,
	type Edge,
	type InternalNode,
} from '@xyflow/react';

import { css, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import {
	useGraphSelection,
	computeEdgeOpacity,
	isHoverRevealedEdge,
} from '../context/GraphSelectionContext';
import { getEdgeParams } from '../layout/floatingEdgeUtils';

export type FloatingEdgeData = {
	/**
	 * Relationship label or verb to render at the edge midpoint
	 * (e.g. "created", "owns"). Hidden until a relevant node is hovered.
	 */
	label?: string;
};

export type FloatingEdgeType = Edge<FloatingEdgeData, 'floatingEdge'>;

const EDGE_COLOR = '#505258';
const PATH_COLOR = '#6ea8fe';
const RAISED_EDGE_Z_INDEX = 60;
const EDGE_BACKING_HEIGHT = 18;
const EDGE_BACKING_ENDPOINT_INSET = 22;
const LABEL_OFFSET_FROM_EDGE = 18;
const ESTIMATED_LABEL_HEIGHT = 22;
const ESTIMATED_LABEL_CHAR_WIDTH = 7;
const ESTIMATED_LABEL_CHROME_WIDTH = 18;
const ESTIMATED_LABEL_MAX_WIDTH = 220;

type Rect = {
	left: number;
	right: number;
	top: number;
	bottom: number;
};

function getNodeRect(node: InternalNode | undefined): Rect | null {
	if (!node) {
		return null;
	}
	const width = node.measured?.width ?? node.width ?? 160;
	const height = node.measured?.height ?? node.height ?? 32;
	const { x, y } = node.internals.positionAbsolute;
	return {
		left: x,
		right: x + width,
		top: y,
		bottom: y + height,
	};
}

function getOverlapArea(a: Rect, b: Rect): number {
	const width = Math.max(0, Math.min(a.right, b.right) - Math.max(a.left, b.left));
	const height = Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top));
	return width * height;
}

function getLabelRect(centerX: number, centerY: number, label: string): Rect {
	const width = Math.min(
		ESTIMATED_LABEL_MAX_WIDTH,
		Math.max(56, label.length * ESTIMATED_LABEL_CHAR_WIDTH + ESTIMATED_LABEL_CHROME_WIDTH),
	);
	const height = ESTIMATED_LABEL_HEIGHT;
	return {
		left: centerX - width / 2,
		right: centerX + width / 2,
		top: centerY - height / 2,
		bottom: centerY + height / 2,
	};
}

function getEndpointOverlapScore(labelRect: Rect, endpointRects: Rect[]): number {
	return endpointRects.reduce((score, rect) => score + getOverlapArea(labelRect, rect), 0);
}

/**
 * HTML pill styling — colour palette mirrors the node hover chip in
 * `StackedObjectNode.tsx` so the relationship label sits at the same
 * visual emphasis as the type chip above each node (dark surface,
 * subtle border, low-emphasis text). Sizing/typography is intentionally
 * NOT mirrored — only the colours, per the design direction.
 *
 * The label is always rendered; opacity is driven dynamically below to
 * provide a subtle fade in / fade out instead of a hard pop.
 */
const edgeLabelStyles = css({
	position: 'absolute',
	// @ts-expect-error -- intentional dark-theme palette mirroring the node hover chip
	backgroundColor: '#101214',
	borderRadius: token('radius.small'),
	borderWidth: token('border.width'),
	borderStyle: 'solid',
	// @ts-expect-error -- intentional dark-theme palette mirroring the node hover chip
	borderColor: '#3D3F43',
	paddingTop: token('space.025'),
	paddingBottom: token('space.025'),
	paddingLeft: token('space.075'),
	paddingRight: token('space.075'),
	// @ts-expect-error -- intentional dark-theme palette mirroring the node hover chip
	color: 'rgba(221,222,225,0.7)',
	pointerEvents: 'none',
	font: token('font.body.small'),
	fontWeight: token('font.weight.medium'),
	// Subtle fade in / fade out so the label doesn't pop on/off when the
	// user moves between nodes.
	transition: 'opacity 0.18s ease-out',
});

const raisedEdgeBackingStyles = css({
	position: 'absolute',
	height: `${EDGE_BACKING_HEIGHT}px`,
	borderRadius: token('radius.full'),
	backgroundImage:
		'linear-gradient(to bottom, rgba(16,18,20,0), rgba(16,18,20,0.72) 50%, rgba(16,18,20,0))',
	pointerEvents: 'none',
	transition: 'opacity 0.18s ease-out',
});

const raisedEdgeLineStyles = css({
	position: 'absolute',
	height: '0px',
	pointerEvents: 'none',
	transition: 'opacity 0.3s ease-out, border-color 0.2s ease-out',
});

export const FloatingEdge: React.NamedExoticComponent<EdgeProps<FloatingEdgeType>> = memo(
	function FloatingEdge({ source, target, style, data }: EdgeProps<FloatingEdgeType>) {
		const sourceNode = useInternalNode(source);
		const targetNode = useInternalNode(target);
		const graphSelection = useGraphSelection();

		if (!sourceNode || !targetNode) {
			return null;
		}

		const { sx, sy, tx, ty } = getEdgeParams(sourceNode, targetNode);
		const edgeOpacity = computeEdgeOpacity(source, target, graphSelection);
		const midX = (sx + tx) / 2;
		const midY = (sy + ty) / 2;
		const length = Math.hypot(tx - sx, ty - sy);
		const angle = Math.atan2(ty - sy, tx - sx);
		const unitX = length > 0 ? (tx - sx) / length : 1;
		const unitY = length > 0 ? (ty - sy) / length : 0;
		const normalX = -unitY;
		const normalY = unitX;
		const backingInset = Math.min(EDGE_BACKING_ENDPOINT_INSET, length / 3);
		const backingLength = Math.max(0, length - backingInset * 2);

		const pairKey = `${source}::${target}`;
		const reversePairKey = `${target}::${source}`;
		const isAnimated =
			graphSelection?.animatedEdgePairs.has(pairKey) ||
			graphSelection?.animatedEdgePairs.has(reversePairKey);
		const strokeColor =
			(style?.stroke as string | undefined) ?? (isAnimated ? PATH_COLOR : EDGE_COLOR);
		const strokeWidth = style?.strokeWidth ?? (isAnimated ? 1.5 : 1);
		const strokeDasharray = style?.strokeDasharray ?? (isAnimated ? '6,4' : undefined);
		const visualOpacity = style?.opacity ?? (isAnimated ? 0.85 : edgeOpacity);

		// Label visibility:
		//   • Selected mode: the hovered node must be a NON-CENTRAL node
		//     (i.e. not the active or centre node) and this edge must be
		//     incident to that hovered neighbour. Hovering the centre/active
		//     node would otherwise reveal every incident relationship at
		//     once, which is overwhelming.
		//   • Default mode (no node selected): hovering ANY node reveals the
		//     verb labels for its 1-hop edges, so the user can read the
		//     surrounding neighbourhood at a glance.
		//
		// We always render the label element so we can drive `opacity` for a
		// smooth fade in / fade out (defined in `edgeLabelStyles`). The DOM
		// node only mounts once a label string is available — without one
		// there is nothing meaningful to fade.
		const verbText = data?.label;
		const showVerb = !!verbText && isHoverRevealedEdge(source, target, graphSelection);
		const endpointRects = [getNodeRect(sourceNode), getNodeRect(targetNode)].filter(
			(rect): rect is Rect => rect !== null,
		);
		const labelOffsetA = {
			x: midX + normalX * LABEL_OFFSET_FROM_EDGE,
			y: midY + normalY * LABEL_OFFSET_FROM_EDGE,
		};
		const labelOffsetB = {
			x: midX - normalX * LABEL_OFFSET_FROM_EDGE,
			y: midY - normalY * LABEL_OFFSET_FROM_EDGE,
		};
		const scoreA = verbText
			? getEndpointOverlapScore(
					getLabelRect(labelOffsetA.x, labelOffsetA.y, verbText),
					endpointRects,
				)
			: 0;
		const scoreB = verbText
			? getEndpointOverlapScore(
					getLabelRect(labelOffsetB.x, labelOffsetB.y, verbText),
					endpointRects,
				)
			: 0;
		const midpointScore = verbText
			? getEndpointOverlapScore(getLabelRect(midX, midY, verbText), endpointRects)
			: 0;
		const useFirstLabelCandidate =
			scoreA < scoreB || (scoreA === scoreB && labelOffsetA.y <= labelOffsetB.y);
		const offsetLabelPosition = useFirstLabelCandidate ? labelOffsetA : labelOffsetB;
		const labelPosition = midpointScore === 0 ? { x: midX, y: midY } : offsetLabelPosition;

		return (
			<React.Fragment>
				<line
					x1={sx}
					y1={sy}
					x2={tx}
					y2={ty}
					stroke="transparent"
					strokeWidth={20}
					style={{ pointerEvents: 'none' }}
				/>
				<line
					x1={sx}
					y1={sy}
					x2={tx}
					y2={ty}
					stroke={isAnimated ? PATH_COLOR : EDGE_COLOR}
					strokeWidth={isAnimated ? 1.5 : 1}
					strokeDasharray={isAnimated ? '6,4' : undefined}
					style={{
						pointerEvents: 'none',
						transition: 'opacity 0.3s ease-out, stroke 0.2s ease-out',
						animation: isAnimated ? 'edgeFlow 0.8s linear infinite' : undefined,
						...style,
						opacity: showVerb ? 0 : visualOpacity,
					}}
				/>
				{verbText ? (
					<EdgeLabelRenderer>
						<React.Fragment>
							<div
								css={raisedEdgeBackingStyles}
								style={{
									// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- dynamic transform + dimensions for the hover-only edge backing
									transform: `translate(-50%, -50%) translate(${midX}px, ${midY}px) rotate(${angle}rad)`,
									width: backingLength,
									opacity: showVerb ? 1 : 0,
									zIndex: RAISED_EDGE_Z_INDEX,
								}}
							/>
							<div
								css={raisedEdgeLineStyles}
								style={{
									// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- dynamic transform + dimensions for the hover-only raised edge
									transform: `translate(-50%, -50%) translate(${midX}px, ${midY}px) rotate(${angle}rad)`,
									width: length,
									borderTopWidth: strokeWidth,
									borderTopStyle: strokeDasharray ? 'dashed' : 'solid',
									borderTopColor: strokeColor,
									opacity: showVerb ? visualOpacity : 0,
									animation: isAnimated ? 'edgeFlow 0.8s linear infinite' : undefined,
									zIndex: RAISED_EDGE_Z_INDEX + 1,
								}}
							/>
							<div
								css={edgeLabelStyles}
								style={{
									// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- dynamic transform + opacity for fade
									transform: `translate(-50%, -50%) translate(${labelPosition.x}px, ${labelPosition.y}px)`,
									opacity: showVerb ? 1 : 0,
									zIndex: RAISED_EDGE_Z_INDEX + 2,
								}}
							>
								{verbText}
							</div>
						</React.Fragment>
					</EdgeLabelRenderer>
				) : null}
			</React.Fragment>
		);
	},
);

FloatingEdge;
